import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Banner } from "@akashnetwork/ui/components";
import { useChainMaintenanceDetails, useGenericBannerDetails, useTopBanner } from "@src/hooks/useTopBanner";
import { FundingBanner } from "./FundingBanner/FundingBanner";
import { TeamPaymentMethodBanner } from "./TeamPaymentMethodBanner/TeamPaymentMethodBanner";
export const NETWORK_DOWN_BANNER_DEPENDENCIES = {
  useChainMaintenanceDetails
};
export function NetworkDownBanner({
  dependencies: d = NETWORK_DOWN_BANNER_DEPENDENCIES
}: {
  dependencies?: typeof NETWORK_DOWN_BANNER_DEPENDENCIES;
} = {}) {
  const {
    date
  } = d.useChainMaintenanceDetails();
  const [isUpgrading, setIsUpgrading] = useState(false);
  useEffect(function trackBlockchainUpgradeWindow() {
    if (!date) {
      setIsUpgrading(false);
      return;
    }
    let timerId: NodeJS.Timeout | undefined;
    function checkIsUpgrading() {
      const hasReachedUpgrade = Date.now() >= new Date(date).getTime();
      setIsUpgrading(hasReachedUpgrade);
      if (!hasReachedUpgrade) {
        timerId = setTimeout(checkIsUpgrading, 60_000);
      }
    }
    checkIsUpgrading();
    return function stopTrackingUpgradeWindow() {
      if (timerId) clearTimeout(timerId);
    };
  }, [date]);
  return <Banner variant="error" data-sentry-element="Banner" data-sentry-component="NetworkDownBanner" data-sentry-source-file="TopBanner.tsx">
      {isUpgrading ? "Network upgrade in progress. Console is read-only until it completes." : "We can't reach Akash Network right now. Console is read-only until service is restored."}
    </Banner>;
}
export const MAINTENANCE_BANNER_DEPENDENCIES = {
  useChainMaintenanceDetails
};
export function MaintenanceBanner({
  onClose,
  dependencies: d = MAINTENANCE_BANNER_DEPENDENCIES
}: {
  onClose: () => void;
  dependencies?: typeof MAINTENANCE_BANNER_DEPENDENCIES;
}) {
  const {
    date
  } = d.useChainMaintenanceDetails();
  const intl = useIntl();
  const upgradeAt = useMemo(() => date ? intl.formatDate(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }) : "", [date, intl]);
  return <Banner variant="warning" onClose={onClose} data-sentry-element="Banner" data-sentry-component="MaintenanceBanner" data-sentry-source-file="TopBanner.tsx">
      Network upgrade scheduled{upgradeAt ? ` at ${upgradeAt}` : ""}. Console will switch to read-only mode during the upgrade.
    </Banner>;
}
export const DEPENDENCIES = {
  useGenericBannerDetails
};
export function GenericBanner({
  onClose,
  dependencies = DEPENDENCIES
}: {
  onClose: () => void;
  dependencies?: typeof DEPENDENCIES;
}) {
  const {
    message,
    links
  } = dependencies.useGenericBannerDetails();
  return <Banner variant="neutral" onClose={onClose} data-sentry-element="Banner" data-sentry-component="GenericBanner" data-sentry-source-file="TopBanner.tsx">
      <span>
        {message}
        {links?.map(link => <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="ml-3 underline hover:opacity-80">
            {link.label}
          </a>)}
      </span>
    </Banner>;
}
export const TOP_BANNER_DEPENDENCIES = {
  useTopBanner,
  NetworkDownBanner,
  MaintenanceBanner,
  GenericBanner,
  TeamPaymentMethodBanner,
  FundingBanner
};

/**
 * The single top banner, rendered inside the (fixed) app header above the nav row — so the nav can never
 * cover it and it can wrap to multiple lines on small screens. Returns null when no banner applies.
 */
export function TopBanner({
  dependencies: d = TOP_BANNER_DEPENDENCIES
}: {
  dependencies?: typeof TOP_BANNER_DEPENDENCIES;
} = {}) {
  const {
    isMaintenanceBannerOpen,
    setIsMaintenanceBannerOpen,
    isGenericBannerOpen,
    setIsGenericBannerOpen,
    isBlockchainDown,
    hasTeamPaymentMethodBanner,
    hasFundingBanner
  } = d.useTopBanner();
  if (isBlockchainDown) {
    return <d.NetworkDownBanner />;
  }
  if (isMaintenanceBannerOpen) {
    return <d.MaintenanceBanner onClose={() => setIsMaintenanceBannerOpen(false)} />;
  }
  if (isGenericBannerOpen) {
    return <d.GenericBanner onClose={() => setIsGenericBannerOpen(false)} />;
  }
  if (hasTeamPaymentMethodBanner) {
    return <d.TeamPaymentMethodBanner />;
  }
  if (hasFundingBanner) {
    return <d.FundingBanner />;
  }
  return null;
}